import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

import { EnvironmentBooking, EnvironmentBookingService } from '../../../../shared/environment-booking.service';

/** The web build the browser is running, shown whatever the booking read returned. */
export interface DeployedBuild {
  commit: string;
  shortCommit: string;
  branch: string;
  builtAt: string;
  url: string;
}

/** Compact internal environment status control rendered by every top-app-bar variant. */
@Component({
  selector: 'app-environment-booking-banner',
  imports: [AsyncPipe, DatePipe, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './environment-booking-banner.component.html',
  styleUrl: './environment-booking-banner.component.scss',
})
export class EnvironmentBookingBannerComponent {
  private static readonly REPOSITORY = 'sanchos101/construction-code-expert';
  private static readonly GITHUB_LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,37}[A-Za-z0-9])?$/;
  private static readonly COMMIT_SHA = /^[0-9a-f]{40}$/;
  private static readonly BUILD_COMMIT = /^[0-9a-f]{7,40}$/;
  @ViewChild('bookingPanel') private bookingPanel?: ElementRef<HTMLElement>;
  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;
  private bookingService = inject(EnvironmentBookingService);
  // Optional so that a host which provides no HTTP client still renders the booking itself.
  private http = inject(HttpClient, { optional: true });
  readonly booking$: Observable<EnvironmentBooking> = this.bookingService.watchBooking();
  /**
   * Comes from the build's own version file, not from the booking, so it stays visible when the
   * booking cannot be read. It says what is deployed; the booking says who reserved the environment.
   */
  readonly build$: Observable<DeployedBuild | null> = this.http
    ? this.http.get<{ gitCommit?: string; gitBranch?: string; buildTimestamp?: string }>('/assets/version.json').pipe(
        map(version => EnvironmentBookingBannerComponent.toBuild(version)),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    : of(null);

  private static toBuild(version: {
    gitCommit?: string;
    gitBranch?: string;
    buildTimestamp?: string;
  }): DeployedBuild | null {
    const commit = (version?.gitCommit ?? '').trim();
    if (!EnvironmentBookingBannerComponent.BUILD_COMMIT.test(commit)) {
      return null;
    }
    const branch = (version.gitBranch ?? '').trim();
    const builtAt = (version.buildTimestamp ?? '').trim();
    return {
      commit,
      shortCommit: commit.slice(0, 7),
      branch: branch && branch !== 'unknown' ? branch : '',
      builtAt: Number.isNaN(Date.parse(builtAt)) ? '' : builtAt,
      url: `https://github.com/${EnvironmentBookingBannerComponent.REPOSITORY}/commit/${commit}`,
    };
  }

  /**
   * Whether to show who holds the environment: for a booking just read, and for the last one read
   * when the latest check got no answer ("not refreshed"), which keeps those details on purpose.
   */
  showsOwner(booking: EnvironmentBooking): boolean {
    return booking.ownershipKnown && (booking.state === 'reserved' || booking.state === 'stale');
  }

  /** A lease was actually read, so what it says about waiting issues can be reported. */
  leaseWasRead(booking: EnvironmentBooking): boolean {
    return (
      !booking.problem && (booking.state === 'reserved' || booking.state === 'free' || booking.state === 'expired')
    );
  }

  checkAgain(): void {
    this.bookingService.refresh();
  }

  issueUrl(booking: EnvironmentBooking): string | null {
    if (!booking.ownershipKnown || booking.repository !== EnvironmentBookingBannerComponent.REPOSITORY) {
      return null;
    }
    return `https://github.com/${booking.repository}/issues/${booking.issue}`;
  }

  profileUrl(booking: EnvironmentBooking): string | null {
    if (!booking.ownershipKnown || !EnvironmentBookingBannerComponent.GITHUB_LOGIN.test(booking.profile)) {
      return null;
    }
    return `https://github.com/${booking.profile}`;
  }

  candidateUrl(booking: EnvironmentBooking): string | null {
    if (
      !booking.ownershipKnown ||
      booking.repository !== EnvironmentBookingBannerComponent.REPOSITORY ||
      !EnvironmentBookingBannerComponent.COMMIT_SHA.test(booking.candidateSha)
    ) {
      return null;
    }
    return `https://github.com/${EnvironmentBookingBannerComponent.REPOSITORY}/commit/${booking.candidateSha}`;
  }

  issueLinkLabel(booking: EnvironmentBooking): string {
    const title = booking.issueTitle ? `: ${booking.issueTitle}` : '';
    return `Issue #${booking.issue}${title}; opens in a new tab`;
  }

  profileLinkLabel(booking: EnvironmentBooking): string {
    return `GitHub profile ${booking.profile}; opens in a new tab`;
  }

  candidateLinkLabel(booking: EnvironmentBooking): string {
    return `Release candidate commit ${booking.candidateSha}; opens in a new tab`;
  }

  abbreviatedCandidate(booking: EnvironmentBooking): string {
    return EnvironmentBookingBannerComponent.COMMIT_SHA.test(booking.candidateSha)
      ? booking.candidateSha.slice(0, 7)
      : booking.candidateSha;
  }

  stateLabel(booking: EnvironmentBooking): string {
    switch (booking.problem) {
      case 'sign-in':
        return 'Sign-in needed';
      case 'not-permitted':
        return 'No access';
      case 'permission-check-failed':
        return 'Can\u2019t check access';
      default:
        break;
    }
    switch (booking.state) {
      case 'reserved':
        return 'In use';
      case 'free':
        return 'Free';
      case 'expired':
        return 'Expired';
      case 'stale':
        return 'Not refreshed';
      case 'malformed':
        return 'Unreadable';
      default:
        return 'No answer';
    }
  }

  icon(booking: EnvironmentBooking): string {
    if (booking.problem === 'sign-in' || booking.problem === 'not-permitted') {
      return 'lock';
    }
    return booking.state === 'free' ? 'check_circle' : booking.state === 'reserved' ? 'construction' : 'warning';
  }

  ariaLabel(booking: EnvironmentBooking): string {
    const owner = booking.ownershipKnown
      ? `Issue ${booking.issue}, profile ${booking.profile}, attempt ${booking.attempt}, phase ${booking.phase}.`
      : 'Ownership unknown.';
    return `Environment ${booking.environment}. ${this.stateLabel(booking)}. ${owner} ${booking.detail}`;
  }

  triggerLabel(booking: EnvironmentBooking): string {
    return `${booking.environment} environment ${this.stateLabel(booking).toLowerCase()}; open booking details`;
  }

  focusMenu(): void {
    queueMicrotask(() => {
      const panel = this.bookingPanel?.nativeElement;
      const firstLink = panel?.querySelector<HTMLElement>('a[href]');
      (firstLink ?? panel)?.focus();
    });
  }

  closeMenu(): void {
    this.menuTrigger?.closeMenu();
  }
}
